import React from 'react';
import { Box, Typography, CircularProgress, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

// Contenedor estilizado para el paginador
const PaginationContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
  borderBottomLeftRadius: theme.shape.borderRadius,
  borderBottomRightRadius: theme.shape.borderRadius,
}));

// Contenedor para los botones de navegación
const NavigationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  gap: theme.spacing(1),
}));

// Botón de navegación estilizado
const NavButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.75),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    transform: 'translateY(-1px)',
  },
  '&:disabled': {
    transform: 'none',
    opacity: 0.5,
  }
}));

/**
 * Componente de paginación tradicional para columnas de Kanban
 * Muestra solo 5 tickets por página con navegación Anterior/Siguiente
 */
const KanbanColumnPagination = ({
  currentPage = 1,          // Página actual (1-indexed)
  totalTickets = 0,         // Total estimado de tickets
  ticketsPerPage = 5,       // Tickets por página
  loading = false,          // Si está cargando
  onPageChange,             // Función para cambiar de página
  hasMore = true           // Si hay más páginas
}) => {
  // 🔧 TEMPORALMENTE: Mostrar siempre que hay tickets para debugging
  if (totalTickets === 0) {
    return null;
  }



  // Calcular información de paginación
  const totalPages = Math.ceil(totalTickets / ticketsPerPage);
  
  // Verificar si hay páginas anterior y siguiente
  const hasPrevious = currentPage > 1;
  const hasNext = currentPage < totalPages || hasMore;

  // Funciones de navegación
  const handlePrevious = () => {
    if (hasPrevious && !loading) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (hasNext && !loading) {
      onPageChange(currentPage + 1);
    }
  };

  return (
    <PaginationContainer>
      {/* Navegación entre páginas - FORZADO PARA DEBUGGING */}
      {totalTickets >= 5 && (
        <NavigationContainer>
          {/* Botón Anterior */}
          <NavButton
            onClick={handlePrevious}
            disabled={!hasPrevious || loading}
            size="small"
            title="Página anterior"
          >
            <KeyboardArrowLeftIcon fontSize="small" />
          </NavButton>

          {/* Indicador de página */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            flex: 1,
            justifyContent: 'center'
          }}>
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="primary" />
                <Typography variant="caption" color="text.secondary">
                  Cargando...
                </Typography>
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Página {currentPage}
              </Typography>
            )}
          </Box>

          {/* Botón Siguiente */}
          <NavButton
            onClick={handleNext}
            disabled={!hasNext || loading}
            size="small"
            title="Página siguiente"
          >
            <KeyboardArrowRightIcon fontSize="small" />
          </NavButton>
        </NavigationContainer>
      )}
    </PaginationContainer>
  );
};

export default KanbanColumnPagination; 